import React from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  useTheme,
  alpha,
  IconButton,
  Button,
} from "@mui/material";
import {
  Close,
  ErrorOutline,
  WarningAmber,
  InfoOutlined,
  CheckCircleOutline,
} from "@mui/icons-material";
import { PopButtonPrimary } from "./PopButton.tsx";

export type AlertSeverity = "error" | "warning" | "info" | "success";

type Props = {
  open: boolean;
  title?: string;
  message: string;
  onClose: () => void;
  severity?: AlertSeverity;
  onConfirm?: () => void;
  confirmLabel?: string;
  cancelLabel?: string;
};

export function SimpleAlertDialog({
  open,
  title,
  message,
  onClose,
  severity = "error",
  onConfirm,
  confirmLabel = "OK",
  cancelLabel = "キャンセル",
}: Props) {
  const theme = useTheme();

  const defaultTitles: Record<AlertSeverity, string> = {
    error: "エラー",
    warning: "警告",
    info: "お知らせ",
    success: "完了",
  };

  const icons: Record<AlertSeverity, React.ReactNode> = {
    error: <ErrorOutline sx={{ fontSize: 48, color: "error.main" }} />,
    warning: <WarningAmber sx={{ fontSize: 48, color: "warning.main" }} />,
    info: <InfoOutlined sx={{ fontSize: 48, color: "info.main" }} />,
    success: <CheckCircleOutline sx={{ fontSize: 48, color: "success.main" }} />,
  };

  const isConfirmMode = Boolean(onConfirm);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, overflow: "hidden", p: 0 },
      }}
    >
      {/* グラデーションヘッダー */}
      <Box
        sx={{
          background: `linear-gradient(to right, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.7)})`,
          color: "#fff",
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "3px solid #fff",
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          {title ?? defaultTitles[severity]}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}>
          <Close />
        </IconButton>
      </Box>
      {/* セパレーターライン */}
      <Box sx={{ height: 3, bgcolor: theme.palette.primary.main }} />

      <DialogContent sx={{ bgcolor: "background.paper", p: 3, textAlign: "center" }}>
        <Box sx={{ mb: 2 }}>{icons[severity]}</Box>
        <Typography variant="body1" sx={{ mb: 3, whiteSpace: "pre-wrap" }}>
          {message}
        </Typography>

        {isConfirmMode ? (
          <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{ minWidth: 100 }}
            >
              {cancelLabel}
            </Button>
            <PopButtonPrimary
              variant="contained"
              onClick={() => {
                onConfirm!();
                onClose();
              }}
              sx={{ minWidth: 100 }}
            >
              {confirmLabel}
            </PopButtonPrimary>
          </Box>
        ) : (
          <PopButtonPrimary
            variant="contained"
            onClick={onClose}
            sx={{ minWidth: 100 }}
          >
            OK
          </PopButtonPrimary>
        )}
      </DialogContent>
    </Dialog>
  );
}
